import { useState } from "react";
import { Button } from "@component/button";
import { ArrowLeftIcon, CheckCircleIcon, DownloadIcon, MapPinIcon, XCircleIcon } from "lucide-react";
import { ContractOffer } from "@type/contract";
import { parseFormat } from "@util/date";
import { contractRespond } from "@service/contract";
import { RejectOfferModal } from "@container/reject-offer-modal";
import { convertCurrency } from "@util/currency";
import { convertContentBreakline, getStatusBadgeClass } from "@util/string";
import { ContractConfirmationDialog } from "./confirm-offer-modal";

interface Props {
  contract: ContractOffer
  onReload: () => void;
  onBack: () => void;
}

export function ContractDetail({
  contract,
  onBack,
  onReload,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [isContractConfirmationOpen, setIsContractConfirmationOpen] =
    useState<boolean>(false);

  const onHandleReject = async (decline_reason: string, message: string, callback: () => void) => {
    try {
      setLoading(true);
      await contractRespond({
        contractId: contract?.id || "",
        status: "Rejected",
        decline_reason: decline_reason,
        message_to_company: message
      });
      callback();
      onReload();
      setIsRejectModalOpen(false)
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change status.");
      setIsRejectModalOpen(false)
      setLoading(false);
    }
  }

  const onHandleConfirm = async (signedContract: File | undefined, callback: () => void) => {
    try {
      setLoading(true);
      await contractRespond({
        contractId: contract?.id || "",
        status: "Accepted",
        signed_contract: signedContract
      });
      callback();
      onReload();
      setIsContractConfirmationOpen(false)
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change status.");
      setIsContractConfirmationOpen(false)
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">Contract not found</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon size={16} />
          Back to Contracts
        </button>

        <div className="bg-gray-50 p-6 rounded-lg mb-6" id="ifj3s6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {contract.job_title}
              </h3>
              <p className="text-gray-700">
                {contract.company_name}
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(contract.status)}`}
            >
              {contract.status}
            </span>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6"
            id="f73em6"
          >
            <div id="dzqwr4">
              <p className="text-sm text-gray-600 mb-1" id="7atxu3">
                Offer Date
              </p>
              <p className="font-medium text-gray-900" id="j9n230">
                {parseFormat(contract.created_at, "D MMMM YYYY")}
              </p>
            </div>
            <div id="u6uvrx">
              <p className="text-sm text-gray-600 mb-1" id="vnpdor">
                Start Date
              </p>
              <p className="font-medium text-gray-900" id="nsl27v">
                {parseFormat(contract.start_date, "D MMMM YYYY")}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">End Date</p>
              <p className="font-medium text-gray-900">
                {contract.end_date ? parseFormat(contract.end_date, "D MMMM YYYY") : "Permanent"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">
                Commitment Type
              </p>
              <p className="font-medium text-gray-900">
                {contract.work_option}
              </p>
            </div>
            <div id="3h6wuy">
              <p className="text-sm text-gray-600 mb-1" id="77k9ig">
                Salary
              </p>
              <p className="font-medium text-gray-900" id="fd9kza">
                {convertCurrency(contract.salary, contract.salary_currency)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Location</p>
              <p className="font-medium text-gray-900 flex items-center gap-1">
                <MapPinIcon size={14} className="text-gray-500" />

                {contract.job_location}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-bold text-gray-900 mb-4">
            Contract Terms
          </h4>
          <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: convertContentBreakline(contract.contract_terms || "") }} />
        </div>
        {contract.signed_contract_s3 || contract.s3_attachment_directory && (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mt-4 gap-2">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Download full contract document
              </p>
              <p className="text-xs text-gray-600">
                Please review all terms and conditions.
              </p>
            </div>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => {
                if (contract.signed_contract_s3) {
                  window.open(`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${contract.signed_contract_s3}`, '_blank');
                } else if (contract.s3_attachment_directory) {
                  window.open(`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${contract.s3_attachment_directory}`, '_blank');
                }
              }}
            >
              <DownloadIcon size={14} id="2he5ux" />
              Download PDF
            </Button>
          </div>
        )}

        {contract.status === "Pending Decision" ? (
          <div className="flex gap-4 mt-8">
            <Button
              variant="default"
              className="flex-1"
              disabled={isLoading}
              onClick={() => setIsContractConfirmationOpen(true)}
            >
              Accept Contract
            </Button>
            <Button
              disabled={isLoading}
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => setIsRejectModalOpen(true)}
            >
              Decline Offer
            </Button>
          </div>
        ) : contract.status === "Accepted" ? (
          <div className="flex gap-4 mt-8">
            <Button
              className="flex-1 bg-green-100 text-green-800 hover:bg-green-100 cursor-not-allowed"
              disabled
            >
              <CheckCircleIcon size={18} className="mr-2" />
              Offer Accepted on {parseFormat(contract.hired_at || "", "D MMMM YYYY")}
            </Button>
          </div>
        ) : contract.status === "Rejected" ? (
          <div className="flex gap-4 mt-8">
            <Button
              className="flex-1 bg-red-100 text-red-800 hover:bg-red-100 cursor-not-allowed"
              disabled
            >
              <XCircleIcon size={18} className="mr-2" />
              Offer Declined on {parseFormat(contract.offer_rejected_by_cand_at || "", "D MMMM YYYY")}
            </Button>
          </div>
        ) : null}
      </div>
      {/* Reject Offer Modal */}
      <RejectOfferModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={onHandleReject}
        contract={contract}
      />

      <ContractConfirmationDialog
        isOpen={isContractConfirmationOpen}
        onClose={() => setIsContractConfirmationOpen(false)}
        onConfirm={onHandleConfirm}
        contract={contract}
      />
    </>
  );
}
